import { action, computed, observable } from "mobx";
import { CodeLocation, EventListenerFields, GeolocationPositionTransferable, InstrumentationEvent, InstrumentationEventAddEventListenerCalled, InstrumentationEventDomEventReceived, isEventTypeForWhichPropsAreReportedOnEveryEvent, MonitoredNavigatorProperty } from "../../lib/Events/InstrumentationEvent";
import { DbRecordedEvent, UUID } from "../../State/EvidenceDB/DatabaseTypes";
import { mapGetOrCreate } from "../../lib/utils/mapGetOrCreate";
import { getRegisteredDomain, urlToDomain } from "../../lib/utils/domains";
import { hasCodeLocation } from "../../lib/utils/CodeLocation";

export const PointerProperties = ['clientX', 'clientY', 'offsetX', 'offsetY', 'pageX', 'pageY', 'screenX', 'screenY'] as const;
export type PointerProperty = typeof PointerProperties[number];
const PointerPropertySet = new Set(PointerProperties);
export const isPointerProperty = (candidate: unknown): candidate is PointerProperty =>
	typeof candidate === "string" && PointerPropertySet.has(candidate.toLocaleLowerCase() as PointerProperty);

export type GeolocationMethod = 'getCurrentPosition' | 'watchPosition';

export const GlobalTags = [
	'window',
	'document',
	'body',
	'html',
] as const;
export type GlobalTag = NonNullable<typeof GlobalTags[number]>;
const GlobalTagSet = new Set(GlobalTags);
export const isGlobalTag = (candidate: unknown): candidate is GlobalTag =>
	typeof candidate === "string" && GlobalTagSet.has(candidate.toLocaleLowerCase() as GlobalTag);

export const KeyEvents = ['keydown', 'keyup', 'keypress'] as const satisfies (keyof WindowEventMap)[];
export type KeyEvent = NonNullable<typeof KeyEvents[number]>;
const KeyEventSet = new Set(KeyEvents);
export const isKeyEvent = (candidate: unknown): candidate is KeyEvent => KeyEventSet.has(candidate as KeyEvent);

export const InputElementEvents = ['input', 'change', 'paste'] as const satisfies (keyof WindowEventMap)[];;
export type InputElementEvent = NonNullable<typeof InputElementEvents[number]>;
const InputElementEventSet = new Set(InputElementEvents);
export const isInputElementEvent = (candidate: unknown): candidate is InputElementEvent =>
	typeof candidate === "string" && InputElementEventSet.has(candidate.toLocaleLowerCase() as InputElementEvent);

export const MouseEvents = ['click', 'mousedown', 'mouseup', 'mousemove',
	'pointermove', 'pointerup', 'pointerdown', 'pointerrawupdate' as 'pointermove'] as const satisfies (keyof WindowEventMap)[];;
export type MouseEvent = NonNullable<typeof MouseEvents[number]>;
const MouseEventSet = new Set(MouseEvents);
export const isMouseEvent = (candidate: unknown): candidate is MouseEvent =>
	typeof candidate === "string" && MouseEventSet.has(candidate.toLocaleLowerCase() as MouseEvent);

export const OtherInterestingEvents = ['devicemotion', 'deviceorientation'] as const;

export type ReportableEvent = KeyEvent | InputElementEvent | MouseEvent;
export const isReportableEvent = (candidate: unknown) =>
	isKeyEvent(candidate) ||
	isInputElementEvent(candidate) ||
	isMouseEvent(candidate);

export const isReportableEventAddEventListener = (candidate: InstrumentationEventAddEventListenerCalled["raw"]): candidate is typeof candidate & EventListenerFields<ReportableEvent> =>
	isReportableEvent(candidate.domOrCustomEventName);

interface ListenerTagInfo {
	tag: string;
	ids: {
		eventListenerUuid: UUID;
		eventIndex: number;
	}[];
}

interface EventListenerRecords<E extends string = string> {
	domEventName: E,
	tags: Map<string, ListenerTagInfo>;
};

type GlobalKeyStrokeRead = {key: string, eventIndex: number, timeStamp: number}
type GlobalKeystrokesRead = Partial<Record<ReportableEvent, GlobalKeyStrokeRead[]>>
type GlobalPropertyReadForTag = {tag: GlobalTag, eventIndexes: number[]};
type GlobalPropertyRead = {property: PointerProperty, tags: Partial<Record<GlobalTag, GlobalPropertyReadForTag>>};
type GlobalPropertiesReadForEvent = Partial<Record<PointerProperty, GlobalPropertyRead>>
type GlobalPropertiesRead = Partial<Record<ReportableEvent, GlobalPropertiesReadForEvent>>

export interface BehaviorsObservedWhenDomainOnCallStack {
	hasCalled?: {method: GeolocationMethod, eventIndexes: number[]}[];
	eventListenersOnGlobalTags?: Map<ReportableEvent, EventListenerRecords<ReportableEvent>>;
	eventListenersOnLocalTags?: Map<string, EventListenerRecords>;
	globalPropertiesRead?: GlobalPropertiesRead;
	globalKeystrokesRead?: GlobalKeystrokesRead;
	// keysObserved?: string[];
	// inputChangesObserved?: string[];
	geolocationPositionsObserved?: {position: GeolocationPositionTransferable, eventIndex: number;}[];
//	keyEventsListenedTo?: InstrumentationEvent
	canvasRenderingContexts?: string[];
	apiConstants?: Partial<Record<MonitoredAPIConstant, string>>
}

export const booleanBehaviorsObservedByRegisteredDomainOnCallStack = (observations: BehaviorsObservedWhenDomainOnCallStack) => {
	const {hasCalled, eventListenersOnGlobalTags, globalPropertiesRead, globalKeystrokesRead} = observations;
	const whenPointerMoved = eventListenersOnGlobalTags?.has('mousemove') || eventListenersOnGlobalTags?.has('pointermove') || false;
	const pointerPress = eventListenersOnGlobalTags?.has('click') || eventListenersOnGlobalTags?.has('mousedown') || eventListenersOnGlobalTags?.has('pointerdown') || false;
	const pointerPosition = globalPropertiesRead != null && Object.keys(globalPropertiesRead).length > 0;
	const whenKeysPressed = eventListenersOnGlobalTags?.has('keydown') || eventListenersOnGlobalTags?.has('keyup') || eventListenersOnGlobalTags?.has('keypress') || false;
	const whichKeysPressed = globalKeystrokesRead != null && Object.keys(globalKeystrokesRead).length > 0;
	const geolocation = hasCalled?.find( x => x.method === 'getCurrentPosition' || x.method === 'watchPosition') != null;
	const canvas = observations.canvasRenderingContexts != null && observations.canvasRenderingContexts.length > 0;
	const deviceMemory = observations.apiConstants != null && observations.apiConstants["Navigator.deviceMemory"] != null;
	const hardwareConcurrency = observations.apiConstants != null && observations.apiConstants["Navigator.hardwareConcurrency"] != null;
	return {
		whenPointerMoved,
		pointerPress,
		pointerPosition,
		whenKeysPressed,
		whichKeysPressed,
		geolocation,
		canvas,
		deviceMemory,
		hardwareConcurrency,
	}
}
export type IsObservedByThirdPartyDomainsCode = ReturnType<typeof booleanBehaviorsObservedByRegisteredDomainOnCallStack>;
export type WhatObservedByThirdPartyDomainsCode = keyof IsObservedByThirdPartyDomainsCode;
export type NumberOfThirdPartiesObserving = {[key in WhatObservedByThirdPartyDomainsCode]: number};

export const getThirdPartyDomainsIntrusivenessScore = (o: IsObservedByThirdPartyDomainsCode) => {
	const boolToBit = (b: boolean) => b ? 1 : 0;
	const fingerprintPoints = boolToBit(o.canvas) + boolToBit(o.deviceMemory) + boolToBit(o.hardwareConcurrency);

	const fingerprinting = fingerprintPoints ** 2;
	const geolocation = boolToBit(o.geolocation) * 3;
	const mouseOrPointer = o.pointerPosition ? 6 : (boolToBit(o.whenPointerMoved) + boolToBit(o.pointerPress));
	const keyboard = o.whichKeysPressed ? 10 : boolToBit(o.whenKeysPressed);
	const total = fingerprinting + geolocation + mouseOrPointer + keyboard;
	return {fingerprinting, geolocation, mouseOrPointer, keyboard, total};
}


export const knownBenignCdnsRegExp: (string | RegExp)[] = [
	/sentry-cdn.com$/i,
	/code.jquery.com$/i
];
export const domainMultipliers = new Map<string, number>();

// export const getOriginsDomainsIntrusivenessScore = (regDomain: string, domainsContactedMap: Map<string, BehaviorsObservedWhenRegisteredDomainOnCallStack>) => {
// 	const domainsContacted = [...domainsContactedMap.entries()];
// 	const thirdPartyDomains = domainsContacted.filter( ([domain]) => domain !== regDomain
// 		&& !knownBenignCdnsRegExp.some( re => typeof re === "string" ? re === domain : re.test(domain))
// 	);
// 	const thirdPartyScores = thirdPartyDomains.map( ([domain, behaviors]) => {
// 		const score = getThirdPartyDomainsIntrusivenessScore(booleanBehaviorsObservedByRegisteredDomainOnCallStack(behaviors));
// 		const domainMultiplier = domainMultipliers.get(domain) ?? 1;
// 		return score.total * domainMultiplier;
// 	}).toSorted()
// 	.toReversed();
// 	return (thirdPartyScores[0] ?? 0) +
// 		(thirdPartyScores[1] ?? 0) * 0.5 +
// 		(thirdPartyScores[2] ?? 0) * 0.25 +
// 		(thirdPartyScores[3] ?? 0) * 0.125;
// }


const hasGeolocationPosition = (existingLocations: GeolocationPositionTransferable[], candidate: GeolocationPositionTransferable) => {
	return null != existingLocations.find( existing => 
		existing.coords.latitude === candidate.coords.latitude &&
		existing.coords.longitude === candidate.coords.longitude &&
		existing.coords.accuracy === candidate.coords.accuracy &&
		existing.coords.altitude === candidate.coords.altitude &&
		existing.coords.altitudeAccuracy === candidate.coords.altitudeAccuracy &&
		existing.coords.heading === candidate.coords.heading &&
		existing.coords.speed === candidate.coords.speed &&
		existing.timestamp === candidate.timestamp
	)
}

export const codeLocationToFirstThirdPartyDomain = (firstPartyRegisteredDomain: string, codeLocation: CodeLocation) => {
	for (const frame of codeLocation.stackArray) {
		const domain = urlToDomain(frame.fileName);
		const registeredDomain = getRegisteredDomain(frame.fileName);
		if (domain != null && registeredDomain != null && registeredDomain !== firstPartyRegisteredDomain) {
			return {domain, registeredDomain};
		}
	}
}

export const hasThirdPartyDomainsOnCallStackBottomFirst = (o: object): o is {thirdPartyDomainsOnCallStackBottomFirst: string[]} =>
	"thirdPartyDomainsOnCallStackBottomFirst" in o &&
	Array.isArray(o.thirdPartyDomainsOnCallStackBottomFirst) &&
	o.thirdPartyDomainsOnCallStackBottomFirst.every( x => typeof x === "string");
;
export const getFirstThirdPartyDomainFromStack = (firstPartyRegisteredDomain: string, objWithThirdPartyDomainsOnCallStackBottomFirst: {thirdPartyDomainsOnCallStackBottomFirst: string[]}) => {
	let domain: string | undefined;
	let registeredDomain: string | undefined;
	for (const candidateThirdPartyDomainOnCallStack of objWithThirdPartyDomainsOnCallStackBottomFirst.thirdPartyDomainsOnCallStackBottomFirst) {
		if (knownBenignCdnsRegExp.some( re => typeof re === "string" ? re === candidateThirdPartyDomainOnCallStack : re.test(candidateThirdPartyDomainOnCallStack))) {
			// This is a known CDN that is not know to collect data, like code.jquery.com,
			// and should be ignored
			continue;
		}
		registeredDomain = getRegisteredDomain(candidateThirdPartyDomainOnCallStack);
		if (registeredDomain != null) {
			domain = candidateThirdPartyDomainOnCallStack;
			break;
		}
	}
	if (domain == null || registeredDomain == null) return;
	return {domain, registeredDomain};
}


export const codeLocationToRegisteredDomains = (codeLocation: CodeLocation) => {
	return [...new Set<string>(codeLocation.stackArray.map( x => {
		try {
			return x.fileName == null ? undefined : getRegisteredDomain( new URL(x.fileName).hostname )
		} catch {
			return undefined;
		}
	}).filter( x => x != null ) )];
}

export type MonitoredAPIConstant = `Navigator.${MonitoredNavigatorProperty}`;
export interface APIsAccessed {
	canvasRenderingContexts?: string[];
	apiConstants?: Partial<Record<MonitoredAPIConstant, string>>
}


export class InstrumentationReportingState {
	highestEventIndexProcessed: number = -1;
	
	#eventListenerIdToBehaviorsObservedRecords = new Map<UUID, BehaviorsObservedWhenDomainOnCallStack>();
	_sourceRegisteredDomainToCodeDomainToInstrumentationData = observable.map<string, {
		sourceRegisteredDomain: string;
		codeDomainToInstrumentationData: Map<string, {domainOnCallStack: string; behaviorsObserved: BehaviorsObservedWhenDomainOnCallStack}>;
		codeRegisteredDomainToInstrumentationData: Map<string, {registeredDomainOnCallStack: string; behaviorsObserved: BehaviorsObservedWhenDomainOnCallStack}>;
	}>();
	_sourceRegisteredDomainToCodeRegisteredDomainToInstrumentationData = observable.map<string,
		Map<string, BehaviorsObservedWhenDomainOnCallStack>
	>();
	@computed get sourceRegDomainToCodeDomainToInstrumentationData() { return this._sourceRegisteredDomainToCodeDomainToInstrumentationData; }

	processInstrumentationEvent = action ( (e: DbRecordedEvent<InstrumentationEvent>) => {
		const {eventIndex} = e;
		const sourceRegisteredDomain = getRegisteredDomain(e.tab.url);
		if (sourceRegisteredDomain == null) return;


		const getBehaviorsObservedForEventsCodeLocations = ({domain, registeredDomain}: {domain: string; registeredDomain: string;}): BehaviorsObservedWhenDomainOnCallStack[]	=> {
			const forSourceRegisteredDomain = mapGetOrCreate(this._sourceRegisteredDomainToCodeDomainToInstrumentationData, sourceRegisteredDomain, ()  => ({
				sourceRegisteredDomain,
				codeDomainToInstrumentationData: observable.map(),
				codeRegisteredDomainToInstrumentationData: observable.map(),
			}));
			const codeDomainToInstrumentationData = mapGetOrCreate(forSourceRegisteredDomain.codeDomainToInstrumentationData, domain, () => ({domainOnCallStack: domain, behaviorsObserved: observable.object({})}));
			const codeRegisteredDomainToInstrumentationData = mapGetOrCreate(forSourceRegisteredDomain.codeRegisteredDomainToInstrumentationData, registeredDomain, () => ({registeredDomainOnCallStack: registeredDomain, behaviorsObserved: observable.object({}) }));
			return [codeDomainToInstrumentationData.behaviorsObserved, codeRegisteredDomainToInstrumentationData.behaviorsObserved] satisfies BehaviorsObservedWhenDomainOnCallStack[];
		}

		const firstThirdPartyRegisteredDomainOnCallStack = hasCodeLocation(e) ?
				codeLocationToFirstThirdPartyDomain(sourceRegisteredDomain, e) :
			hasThirdPartyDomainsOnCallStackBottomFirst(e) ?
				getFirstThirdPartyDomainFromStack(sourceRegisteredDomain, e) :
				undefined;
		
		const behaviorsObservedAtCodeLocations = (firstThirdPartyRegisteredDomainOnCallStack != null) ? getBehaviorsObservedForEventsCodeLocations(firstThirdPartyRegisteredDomainOnCallStack) : [];

		switch (e.eventName) {
			case "AccessedNavigatorProperty": {
				const {property, value} = e.raw;
				for (const behaviorsObservedAtCodeLocation of behaviorsObservedAtCodeLocations) {
					behaviorsObservedAtCodeLocation.apiConstants ??= observable.object({} as Partial<Record<MonitoredAPIConstant, string>>);
					behaviorsObservedAtCodeLocation.apiConstants[`Navigator.${property}`] = `${value}`;
				}
				break;
			}
			case "AccessedCanvasRenderingContext": {
				for (const behaviorsObservedAtCodeLocation of behaviorsObservedAtCodeLocations) {
					behaviorsObservedAtCodeLocation.canvasRenderingContexts ??= observable.array();
					behaviorsObservedAtCodeLocation.canvasRenderingContexts.push(e.raw.contextType);
				}
				break;
			}
			case "GeolocationRequest.getCurrentPosition":
			case "GeolocationRequest.watchPosition": {
				const method = e.eventName.split(e.eventName)[1]  as GeolocationMethod;
				for (const behaviorsObservedAtCodeLocation of behaviorsObservedAtCodeLocations) {
					behaviorsObservedAtCodeLocation.hasCalled ??= observable.array();
					let methodRecord = behaviorsObservedAtCodeLocation.hasCalled.find( hc => hc.method === method );
					if (methodRecord == null) {
						behaviorsObservedAtCodeLocation.hasCalled.push( methodRecord = {method, eventIndexes: observable.array()} );
					}
					methodRecord.eventIndexes.push(eventIndex);
				}
				break;
			}

			case "GeolocationRequest.getCurrentPosition.success":
			case "GeolocationRequest.watchPosition.success": {
				// const method = e.eventName.split(e.eventName)[1]  as GeolocationMethod;
				const {position} = e.raw;
				for (const behaviorsObservedAtCodeLocation of behaviorsObservedAtCodeLocations) {
					const geolocationPositionsObserved = behaviorsObservedAtCodeLocation.geolocationPositionsObserved ??= observable.array();
					if (!hasGeolocationPosition(geolocationPositionsObserved.map( r => r.position), position)) {
						behaviorsObservedAtCodeLocation.geolocationPositionsObserved.push({position, eventIndex});
					}
				}
				break;
			}

			case "AddEventListenerCalled": {
				const {domOrCustomEventName: domEventName, elementTag, eventListenerUuid} = e.raw;
				if (isReportableEventAddEventListener(e.raw)) {
					for (const behaviorsObservedAtCodeLocation of behaviorsObservedAtCodeLocations) {
						this.#eventListenerIdToBehaviorsObservedRecords.set(eventListenerUuid, behaviorsObservedAtCodeLocation);
						const isListeningEventOnTags = isGlobalTag(elementTag) ?
							(behaviorsObservedAtCodeLocation.eventListenersOnGlobalTags ??= observable.map()) :
							(behaviorsObservedAtCodeLocation.eventListenersOnLocalTags ??= observable.map());
						const isListeningToEvent = mapGetOrCreate(isListeningEventOnTags, domEventName, () => (observable({domEventName: domEventName, tags: observable.map()})));
						let listeningToTag = mapGetOrCreate(isListeningToEvent.tags, elementTag, () => (observable({tag: elementTag, ids: observable.array()})));
						listeningToTag.ids.push({eventIndex, eventListenerUuid});
					}
					if (isGlobalTag(elementTag) && e.raw.propsRead != null) {
						for (const behaviorsObservedAtCodeLocation of behaviorsObservedAtCodeLocations) {
							for (const property of PointerProperties) {
								const propsRead = e.raw.propsRead as string[];
								if (propsRead.includes(property)) {
									const globalMousePropertiesRead: GlobalPropertiesRead = behaviorsObservedAtCodeLocation.globalPropertiesRead ??= observable({});
									const globalMousePropertiesReadForEvent: GlobalPropertiesReadForEvent = globalMousePropertiesRead[e.raw.domOrCustomEventName] ??= observable({});
									const propertyRead: GlobalPropertyRead = globalMousePropertiesReadForEvent[property] ??= observable({property, tags: observable({})});
									const tag = propertyRead.tags[elementTag] ??= observable({tag: elementTag, eventIndexes: observable.array() as number[]});
									tag.eventIndexes.push(eventIndex);
								}
							}
						}
					}
				}
				break;
			}

			case "DomEventReceived": {
				if (isKeyEvent(e.raw.domOrCustomEventName)) {
					const {domOrCustomEventName: domEventName, elementTag: tag, eventListenerUuid, domEvent} = e.raw as InstrumentationEventDomEventReceived<KeyEvent>["raw"];
					const propsRead = e.raw.propsRead as string[];
					if (isGlobalTag(tag) && isEventTypeForWhichPropsAreReportedOnEveryEvent(e.raw.domOrCustomEventName)) {
						const key = propsRead.includes("key") ? domEvent.key :
								propsRead.includes("charCode") ? String.fromCharCode(domEvent.charCode) :
								propsRead.includes("keyCode") ? String.fromCharCode(domEvent.keyCode) :
								propsRead.includes("code") ? domEvent.code :
								undefined;
						if (key != null) {
							const record = {key, eventIndex, timeStamp: domEvent.timeStamp};
							for (const behaviorsObservedAtCodeLocation of behaviorsObservedAtCodeLocations) {
								const globalKeystrokesRead: GlobalKeystrokesRead = behaviorsObservedAtCodeLocation.globalKeystrokesRead ??= observable({});
								const keystrokesReadForEvent = globalKeystrokesRead[domEventName] ??= observable.array();
								const recordIndex = keystrokesReadForEvent.findIndex( x => x.eventIndex === eventIndex);
								if (recordIndex >= 0) {
									keystrokesReadForEvent[recordIndex] = record;
								} else  {
									const last = keystrokesReadForEvent[keystrokesReadForEvent.length - 1];
									if (last == null || last.key !== key || last.timeStamp !== domEvent.timeStamp) {										
										keystrokesReadForEvent.push(record);
									}
								}
							}
						}
					}
				}
				break;
			}
		}
	});


	constructor() {}

}

